import { OccurrenceStepEnum, getOccurrenceStepLabelId } from '../../enums/occurrenceStep';
import type { Occurrence, OccurrenceStep, OccurrenceWithTimeSpent } from '../../models/occurrence';
import type { Step } from '../../models/step';
import { convertTimeStringToMilliseconds } from '../../utils/dateUtils';
import type { StageSummary } from './types';

export type TrackingPersona = 'TOWER' | 'CONSULTANT' | 'MANAGER' | 'PARTNERSHIP';

export type TrackingView = 'table' | 'board' | 'kanban';

export type PeriodPreset = 'today' | '7d' | '30d' | 'all';

export type SearchField = 'CHASSIS' | 'LICENSE_PLATE' | 'CUSTOMER' | 'PROTOCOL' | 'ASSET_NAME';

export type StepState = 'done' | 'current' | 'todo';

export interface DateRange {
    start?: string;
    end?: string;
}

export const SEARCH_FIELDS: { value: SearchField; labelId: string; placeholderId: string }[] = [
    {
        value: 'CHASSIS',
        labelId: 'intl-msg:kanban.filterRow.chassis',
        placeholderId: 'intl-msg:kanban.filterRow.searchByChassis',
    },
    {
        value: 'LICENSE_PLATE',
        labelId: 'intl-msg:kanban.filterRow.licensePlate',
        placeholderId: 'intl-msg:kanban.filterRow.searchByLicensePlate',
    },
    {
        value: 'CUSTOMER',
        labelId: 'intl-msg:kanban.filterRow.customer',
        placeholderId: 'intl-msg:kanban.filterRow.searchByCustomer',
    },
    {
        value: 'PROTOCOL',
        labelId: 'intl-msg:kanban.filterRow.protocol',
        placeholderId: 'intl-msg:kanban.filterRow.searchByProtocol',
    },
    {
        value: 'ASSET_NAME',
        labelId: 'intl-msg:kanban.filterRow.assetName',
        placeholderId: 'intl-msg:kanban.filterRow.searchByAssetName',
    },
];

export const CRITICALITY_LEVELS: { value: string; labelId: string; tone: string }[] = [
    { value: '1', labelId: 'intl-msg:kanban.filterRow.criticalityNotUrgent', tone: 'c1' },
    { value: '2', labelId: 'intl-msg:kanban.filterRow.criticalityLowUrgency', tone: 'c2' },
    { value: '3', labelId: 'intl-msg:kanban.filterRow.criticalityUrgent', tone: 'c3' },
    { value: '4', labelId: 'intl-msg:kanban.filterRow.criticalityVeryUrgent', tone: 'c4' },
    { value: '5', labelId: 'intl-msg:kanban.filterRow.criticalityEmergency', tone: 'c5' },
];

export const getCriticality = (value?: number | string) =>
    CRITICALITY_LEVELS.find(level => level.value === String(value ?? ''));

export const TYPE_OPTIONS: { value: string; labelId: string }[] = [
    { value: 'PREVENTIVE', labelId: 'intl-msg:kanban.filterRow.preventive' },
    { value: 'CORRECTIVE', labelId: 'intl-msg:kanban.filterRow.corrective' },
    { value: 'HAS_CAMPAIGNS', labelId: 'intl-msg:kanban.filterRow.fieldCampaigns' },
    { value: 'ASSISTANCE', labelId: 'intl-msg:kanban.filterRow.roadAssistance' },
    { value: 'WINCH', labelId: 'intl-msg:kanban.filterRow.winch' },
    { value: 'IN_DEALERSHIP', labelId: 'intl-msg:kanban.filterRow.inDealership' },
    { value: 'CLAIM', labelId: 'intl-msg:kanban.filterRow.claim' },
];

const OCCURRENCE_TYPE_LABEL: Record<string, string> = {
    P: 'intl-msg:kanban.filterRow.preventive',
    PREVENTIVE: 'intl-msg:kanban.filterRow.preventive',
    C: 'intl-msg:kanban.filterRow.corrective',
    CORRECTIVE: 'intl-msg:kanban.filterRow.corrective',
    A: 'intl-msg:kanban.filterRow.roadAssistance',
    ASSISTANCE: 'intl-msg:kanban.filterRow.roadAssistance',
    W: 'intl-msg:kanban.filterRow.winch',
    WINCH: 'intl-msg:kanban.filterRow.winch',
    D: 'intl-msg:kanban.filterRow.inDealership',
    IN_DEALERSHIP: 'intl-msg:kanban.filterRow.inDealership',
    CL: 'intl-msg:kanban.filterRow.claim',
    CLAIM: 'intl-msg:kanban.filterRow.claim',
};

export const getOccurrenceTypeLabelId = (type?: string) => (type ? OCCURRENCE_TYPE_LABEL[type] : undefined);

export const toStepValue = (stepKey: string) => OccurrenceStepEnum[stepKey as keyof typeof OccurrenceStepEnum];

export const getStepLabelId = (step?: string) => (step ? getOccurrenceStepLabelId(step) : undefined);

export const getCurrentStep = (occurrence: Occurrence): string | undefined => {
    if (occurrence.stepTypeCurrent) {
        return occurrence.stepTypeCurrent;
    }
    return (occurrence.steps ?? [])
        .filter(step => step.step && step.dtStart && !step.dtEnd)
        .sort((a, b) => (b.dtStart?.getTime() ?? 0) - (a.dtStart?.getTime() ?? 0))[0]?.step;
};

export const findStep = (occurrence: Occurrence, step: string): OccurrenceStep | undefined =>
    occurrence.steps?.find(item => item.step === step);

export const getStepState = (occurrence: Occurrence, step: string): StepState => {
    const record = findStep(occurrence, step);
    if (!record?.dtStart) {
        return 'todo';
    }
    return record.dtEnd ? 'done' : 'current';
};

export const isNextStampOccurrence = (occurrence: Occurrence) => {
    const isPreventive = occurrence.occurrenceType === 'P' || occurrence.occurrenceType === 'PREVENTIVE';
    const isDealer = occurrence.occurrenceType === 'IN_DEALERSHIP' || occurrence.occurrenceType === 'D';
    return isPreventive || (isDealer && !!occurrence.volksEssentialsPlanContract);
};

export const getStepElapsedMs = (record?: OccurrenceStep, now = Date.now()) => {
    const lastLog = record?.logs?.[record.logs.length - 1];
    const start = lastLog?.startDate ?? record?.dtStart;
    if (!start) {
        return undefined;
    }
    const end = lastLog ? (record?.dtEnd ? lastLog.endDate : undefined) : record?.dtEnd;
    return Math.max(0, new Date(end ?? now).getTime() - new Date(start).getTime());
};

export const getStepEstimateMs = (steps: Step[], step: string) => {
    const hours = steps.find(item => item.id === step)?.elapsedHours ?? 0;
    return hours > 0 ? hours * 3600 * 1000 : undefined;
};

export const getStepTiming = (occurrence: Occurrence, steps: Step[], step: string) => {
    const elapsed = getStepElapsedMs(findStep(occurrence, step));
    const estimate = getStepEstimateMs(steps, step);
    return { elapsed, estimate, late: elapsed !== undefined && estimate !== undefined && elapsed > estimate };
};

export const toDurationMs = (value?: string) => {
    if (!value) {
        return undefined;
    }
    const ms = convertTimeStringToMilliseconds(value);
    return Number.isNaN(ms) ? undefined : ms;
};

export const getOccurrenceTotalMs = (occurrence: Occurrence, timeSpent?: OccurrenceWithTimeSpent) => {
    if (timeSpent?.timeSpent !== undefined) {
        return timeSpent.timeSpent * 1000;
    }
    return occurrence.timeSpent !== undefined ? occurrence.timeSpent * 60 * 1000 : undefined;
};

const getStageRatio = (stage: StageSummary) =>
    stage.averageMs && stage.estimateMs ? stage.averageMs / stage.estimateMs : 0;

export const getStageHeat = (stage: StageSummary) => {
    const ratio = getStageRatio(stage);
    const tone = ratio > 1.1 ? 'mt-heat--bad' : ratio > 0.95 ? 'mt-heat--warn' : 'mt-heat--ok';
    const width = Math.min(100, Math.max(6, ratio * 100));
    return { tone, width: ratio ? width : 0 };
};

export const toggleItem = <T>(list: T[], value: T) =>
    list.includes(value) ? list.filter(item => item !== value) : [...list, value];

export const sameList = (a: string[], b: string[]) =>
    a.length === b.length && a.every((item, index) => item === b[index]);

export const sameRange = (a: DateRange | null, b: DateRange | null) =>
    (a?.start ?? '') === (b?.start ?? '') && (a?.end ?? '') === (b?.end ?? '');

const startOfToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};

export const getPeriodRange = (preset: PeriodPreset): DateRange | null => {
    if (preset === 'all') {
        return null;
    }
    const today = startOfToday();
    const start = new Date(today);
    if (preset === '7d') {
        start.setDate(today.getDate() - 6);
    }
    if (preset === '30d') {
        start.setDate(today.getDate() - 29);
    }
    const endExclusive = new Date(today);
    endExclusive.setDate(endExclusive.getDate() + 1);
    return { start: start.toISOString(), end: endExclusive.toISOString() };
};

const fromDateInput = (value: string) => {
    const [year, month, day] = value.split('-').map(Number);
    return year && month && day ? new Date(year, month - 1, day) : undefined;
};

export const toDateInput = (iso?: string) => {
    if (!iso) {
        return '';
    }
    const date = new Date(iso);
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const toRangeFromInputs = (from: string, to: string): DateRange | null => {
    const start = from ? fromDateInput(from) : undefined;
    const endDay = to ? fromDateInput(to) : undefined;
    if (!start && !endDay) {
        return null;
    }
    const endExclusive = endDay ? new Date(endDay) : undefined;
    endExclusive?.setDate(endExclusive.getDate() + 1);
    return { start: start?.toISOString(), end: endExclusive?.toISOString() };
};

export const toRangeInputs = (range: DateRange | null) => {
    if (!range?.start && !range?.end) {
        return { from: '', to: '' };
    }
    const endInclusive = range.end ? new Date(range.end) : undefined;
    endInclusive?.setDate(endInclusive.getDate() - 1);
    return { from: toDateInput(range.start), to: endInclusive ? toDateInput(endInclusive.toISOString()) : '' };
};

export type DayPreset = 'today' | 'yesterday' | 'last3Days' | 'last10Days';

export const DAY_PRESETS: { key: DayPreset; labelId: string; fromDaysAgo: number; toDaysAgo: number }[] = [
    { key: 'today', labelId: 'intl-msg:kanban.filterRow.today', fromDaysAgo: 0, toDaysAgo: 0 },
    { key: 'yesterday', labelId: 'intl-msg:kanban.filterRow.yesterday', fromDaysAgo: 1, toDaysAgo: 1 },
    { key: 'last3Days', labelId: 'intl-msg:kanban.filterRow.last3Days', fromDaysAgo: 2, toDaysAgo: 0 },
    { key: 'last10Days', labelId: 'intl-msg:kanban.filterRow.last10Days', fromDaysAgo: 9, toDaysAgo: 0 },
];

const daysAgoInput = (days: number) => {
    const date = startOfToday();
    date.setDate(date.getDate() - days);
    return toDateInput(date.toISOString());
};

export const getDayPresetInputs = (preset: DayPreset) => {
    const config = DAY_PRESETS.find(item => item.key === preset);
    return config
        ? { from: daysAgoInput(config.fromDaysAgo), to: daysAgoInput(config.toDaysAgo) }
        : { from: '', to: '' };
};

export const matchDayPreset = (from: string, to: string) =>
    DAY_PRESETS.find(item => {
        const range = getDayPresetInputs(item.key);
        return range.from === from && range.to === to;
    })?.key;

export const matchPeriodPreset = (range: DateRange | null): PeriodPreset | undefined => {
    if (!range?.start && !range?.end) {
        return 'all';
    }
    return (['today', '7d', '30d'] as PeriodPreset[]).find(preset => {
        const candidate = getPeriodRange(preset);
        return candidate?.start === range.start && candidate?.end === range.end;
    });
};
